import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import BookCard from "@/components/BookCard";
import ShareButton from "@/components/ShareButton";
import ReadingStats from "@/components/ReadingStats";
import LibraryTabs from "@/components/LibraryTabs";
import { useAuth } from "@/contexts/AuthContext";

export default function Historial() {
    const { t, i18n } = useTranslation();
    const { loading: authLoading } = useAuth();
    const [items, setItems] = useState(null);
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        (async () => {
            try {
                const { data } = await api.get("/history");
                setItems(data);
                if (data.length && !openId) setOpenId(data[0].id);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
                setItems([]);
            }
        })();
    }, [authLoading, openId]);

    const handleDelete = async (id) => {
        try {
            await api.delete(`/history/${id}`);
            setItems((xs) => xs.filter((x) => x.id !== id));
            toast.success(t("historial.deleted"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (authLoading || items === null) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex items-center justify-center">
                <p className="font-display text-2xl text-[var(--ink)] animate-slow-pulse italic">
                    {t("leidos.loading")}
                </p>
            </main>
        );
    }

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                    {t("historial.eyebrow")}
                </p>
                <h1
                    className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight"
                    style={{ fontVariationSettings: '"wght" 700, "wdth" 75, "opsz" 144' }}
                >
                    {t("historial.title")}
                </h1>
                <p className="text-[var(--ink-muted)] mt-4 text-lg">{t("historial.lead")}</p>
            </div>

            <LibraryTabs />

            <div className="mb-10">
                <ReadingStats />
            </div>

            {items.length === 0 ? (
                <div
                    data-testid="empty-library-state"
                    className="text-center py-24 border border-[var(--border-strong)] bg-[var(--bg-elevated)]"
                >
                    <BookOpen size={36} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                    <p
                        className="font-display text-3xl text-[var(--ink)] mb-3"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}
                    >
                        {t("historial.empty_title")}
                    </p>
                    <p className="text-[var(--ink-muted)] mb-8 max-w-md mx-auto">
                        {t("historial.empty_lead")}
                    </p>
                    <Link
                        to="/"
                        data-testid="empty-library-cta"
                        className="inline-block bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                    >
                        {t("historial.empty_cta")}
                    </Link>
                </div>
            ) : (
                <div className="space-y-8">
                    {items.map((item) => {
                        const isOpen = openId === item.id;
                        return (
                            <article
                                key={item.id}
                                data-testid={`history-item-${item.id}`}
                                className="border border-[var(--border-strong)] bg-[var(--bg-elevated)]"
                            >
                                <header className="flex flex-wrap items-start justify-between gap-4 p-6 md:p-8">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-2">
                                            {t(`preference_labels.${item.preference}`)}
                                            {" · "}
                                            {new Date(item.created_at).toLocaleDateString(i18n.language, {
                                                day: "numeric",
                                                month: "long",
                                                year: "numeric",
                                            })}
                                        </p>
                                        <h2
                                            className="font-display text-3xl md:text-4xl text-[var(--ink)] italic truncate"
                                            style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 96, "slnt" -10' }}
                                        >
                                            {item.based_on}
                                        </h2>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            data-testid={`toggle-history-${item.id}`}
                                            onClick={() => setOpenId(isOpen ? null : item.id)}
                                            className="text-sm text-[var(--ink-muted)] hover:text-[var(--accent)] underline underline-offset-4"
                                        >
                                            {isOpen ? t("historial.hide") : t("historial.show")}
                                        </button>
                                        <ShareButton recId={item.id} basedOn={item.based_on} />
                                        <button
                                            data-testid={`delete-history-${item.id}`}
                                            onClick={() => handleDelete(item.id)}
                                            className="text-[var(--ink-dim)] hover:text-[var(--danger)] transition-colors"
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={16} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </header>
                                {isOpen && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 p-6 md:p-8 pt-0">
                                        {item.recommendations.map((book, i) => (
                                            <BookCard
                                                key={book.id}
                                                book={book}
                                                index={i}
                                                recommendationId={item.id}
                                            />
                                        ))}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </main>
    );
}
